/**
 * Service centralisé pour les appels API géographiques
 * Gère les appels à l'API geo.les-menus-services.com
 */

const GEO_API_BASE_URL  = `${process.env.NEXT_PUBLIC_GEO_API_URL}`;

interface GeoApiOptions {
  headers?: Record<string, string>;
  method?: 'GET' | 'POST';
}

/**
 * Types pour les réponses API
 */
export interface Prediction {
  description: string;
  placeId: string;
  types: string[];
}

export interface AddressDetails {
  street: string;
  postcode: string;
  city: string;
  idSalesforce: string;
  // Champs pour les agences
  slug?: string;
  id?: number;
  lmsName?: string;
  name?: string;
  adresse?: string;
  phone?: string;
  phoneIdelio?: string;
  showIdelioButton: boolean;
  url_agence: string;

  // Ajouts pour gérer le fallback
  message?: string;
  isNearestAgency?: boolean;
}

export interface PlaceDetails {
  name?: string;
  address?: string;
  [key: string]: unknown; 
}

/**
 * Classe de service pour les appels API géographiques
 */
class GeoService {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  /**
   * Méthode privée pour effectuer les requêtes HTTP
   */
  private async request<T>(endpoint: string, options?: GeoApiOptions): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    try {
      const response = await fetch(url, {
        method: options?.method || 'GET',
        headers: {
          'Accept': 'application/json',
          ...options?.headers,
        },
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error calling ${endpoint}:`, error);
      throw error;
    }
  }

  /**
   * Récupérer les prédictions d'adresse basées sur une requête
   * @param query - La requête de recherche (minimum 3 caractères recommandé)
   * @returns Liste des prédictions d'adresse
   */
  async getPredictions(query: string): Promise<Prediction[]> {
    return this.request<Prediction[]>(`/getpredictions/${query}`);
  }

  /**
   * Récupérer les détails d'une adresse via son placeId
   * @param placeId - L'identifiant unique du lieu
   * @returns Détails de l'adresse (rue, code postal, ville, ID Salesforce)
   */
  async getMap(placeId: string): Promise<AddressDetails> {
    return this.request<AddressDetails>(`/getmap/${placeId}`);
  }

  /**
   * Récupérer les détails complets d'un lieu via son placeId
   * @param placeId - L'identifiant unique du lieu
   * @returns Détails complets du lieu
   */
  async getPlace(placeId: string): Promise<PlaceDetails> {
    return this.request<PlaceDetails>(`/getplace/${placeId}`);
  }

/**
   * Récupérer l'agence la plus proche d'une adresse via son placeId
   * @param placeId - L'identifiant unique du lieu
   * @returns Détails de l'agence la plus proche
   */

  async getNearestAgency(placeId: string): Promise<AddressDetails  | null> {
    const response = await this.request<AddressDetails>(`/nearestagency/${placeId}`);
    console.log("Réponse de l'API nearestagency:", response);
    if (Array.isArray(response)) {
    return response.length > 0 ? response[0] : null;
  }

  return response ?? null;
  }

  
}

/**
 * Instance singleton du service géographique
 * À utiliser dans toute l'application
 */
export const geoService = new GeoService(GEO_API_BASE_URL);